import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,

} from '@/components/shared/ui/navigation-menu';
import * as React from 'react';

import { useNavigationData } from '@/features/user/hooks';
import { buildTree } from '@/lib/treeUtils';

type Category = {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  children?: Category[];
};

type Collection = {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  children?: Collection[];
};

const ListItem = React.forwardRef<
  React.ElementRef<typeof Link>,
  React.ComponentPropsWithoutRef<typeof Link> & {
    title: string;
    children?: React.ReactNode;
  }
>(({ className, title, children, to, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          to={to}
          className={cn(
            "font-jost block select-none space-y-1 rounded-[8px] p-2 leading-none no-underline  transition-colors  hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground bg-transparent hover:bg-[#D49499]/25",
            className
          )}
          {...props}
        >
          <div className="text-[16px] font-jost text-black-200 hover:text-black-800 transition-colors ">{title}</div>
          {children && (
            <p className="line-clamp-2 text-[14px] font-jost leading-snug text-muted-foreground">
              {children}
            </p>
          )}
        </Link>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export const MegaMenu = () => {
  const { categories: rawCategories, collections: rawCollections, loading } = useNavigationData();
  const [categories, setCategories] = useState<Category[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);

  useEffect(() => {
    if (!loading) {
      setCategories(buildTree(rawCategories));
      setCollections(buildTree(rawCollections));
    }
  }, [rawCategories, rawCollections, loading]);

  const [isOpen, setIsOpen] = useState(false);
  const hasData = !loading && (categories.length > 0 || collections.length > 0);

  return (
    <>
      {/* Desktop - NavigationMenu desde lg en adelante */}
      <div className="hidden lg:block">
        {hasData ? (
          <NavigationMenu >
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="font-normal text-black-500 sm:text-[14px]  xl:text-[15px] font-jost hover:text-primary transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent">
                  <Link
                    to="/Tienda"
                    className="p-3 min-h-[48px] font-jost font-normal text-black-500 hover:text-primary transition-colors tracking-[0.5px] flex items-center justify-center"
                  >
                    TIENDA
                  </Link>
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-1 w-[95vw] lg:w-[62rem] max-w-[1100px] grid-cols-[1fr_1fr_3fr]">
                    {/* Categorías */}
                    <div className="col-span-1 px-2 py-4">
                      <h3 className="mb-3 ml-1.5 text-sm font-semibold font-jost">CATEGORIAS</h3>
                      <div className="space-y-0">
                        {categories.slice(0, 4).map((category) => (
                          <ListItem
                            key={category.id}
                            title={category.name}
                            to={`/tienda?categoria=${category.slug}`}
                          >
                            {category.children && category.children.length > 0
                              ? `${category.children.length} subcategorías`
                              : ''}
                          </ListItem>
                        ))}
                        {categories.length > 4 && (
                          <ListItem to="/categorias" title="Ver todas">
                            Todas las categorías disponibles
                          </ListItem>
                        )}
                      </div>
                    </div>

                    {/* Colecciones */}
                    <div className="col-span-1 px-3 py-4">
                      <h3 className="mb-3 ml-1.5 text-sm font-semibold font-jost">COLECCIONES</h3>
                      <div className="space-y-1">
                        {collections.slice(0, 4).map((collection) => (
                          <ListItem
                            key={collection.id}
                            title={collection.name}
                            to={`/tienda?coleccion=${collection.slug}`}
                          >
                            {collection.children && collection.children.length > 0
                              ? `${collection.children.length} subcolecciones`
                              : ''}
                          </ListItem>
                        ))}
                        {collections.length > 4 && (
                          <ListItem to="/colecciones" title="Ver todas">
                            Todas las colecciones
                          </ListItem>
                        )}
                      </div>
                    </div>
                    <li className="row-span-4">
                      <NavigationMenuLink asChild>
                        <Link
                          className="flex h-full w-full select-none flex-col justify-end  bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md relative overflow-hidden"
                          to="/tienda"
                        >
                          <div className="absolute inset-0 w-full h-full">
                            <img
                              src="/images/djordje-cvetkovic.webp"
                              alt="Tienda"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent" />
                          </div>
                          <div className="relative z-10 mb-2 mt-4 text-xl font-medium font-jost text-white  underline  transition duration-150 ease-in-out">
                            TIENDA
                          </div>
                          <p className="relative z-10 text-sm text-white/50 font-jost">
                            Descubre nuestra colección completa de productos únicos y de alta calidad.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
            <NavigationMenuViewport />
          </NavigationMenu>
        ) : (
          <Link
            to="/Tienda"
            className="p-3 min-h-[48px] font-jost font-normal text-black-500 hover:text-primary transition-colors tracking-[0.5px] flex items-center justify-center uppercase sm:text-[14px] xl:text-[15px]"
          >
            TIENDA
          </Link>
        )}
      </div>

      {/* Mobile/Tablet - Diseño original hasta lg */}
      <div className="lg:hidden">
        {hasData ? (
          <>
            <button className="flex items-center px-4 py-3 min-h-[48px] w-full font-jost text-black-500 hover:text-primary transition-colors tracking-[0.5px] text-[13px]" onClick={() => setIsOpen(!isOpen)}>
              <span>TIENDA</span>
              <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-2 ml-3 font-jost">
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase font-jost">Categorías</h3>
                <ul className="space-y-1 mb-3">
                  {categories.map((category) => (
                    <li key={category.id}>
                      <Link
                        to={`/tienda?categoria=${category.slug}`}
                        className="font-jost block px-4 text-gray-900 rounded transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {category.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase">Colecciones</h3>
                <ul className="space-y-1 mb-3">
                  {collections.slice(0, 3).map((collection) => (
                    <li key={collection.id}>
                      <Link
                        to={`/tienda?coleccion=${collection.slug}`}
                        className="block px-4 text-gray-900 rounded transition-colors font-jost"
                        onClick={() => setIsOpen(false)}
                      >
                        {collection.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase">Más Vendidos</h3>
                <ul className="space-y-1">
                  <li>
                    <Link
                      to="/productos-mas-vendidos"
                      className="block px-4 text-gray-900 rounded transition-colors font-jost"
                      onClick={() => setIsOpen(false)}
                    >
                      Blusa Elegante Rosa
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/productos-mas-vendidos"
                      className="block px-4 text-gray-900 rounded transition-colors font-jost"
                      onClick={() => setIsOpen(false)}
                    >
                      Conjunto Deportivo Pro
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/productos-mas-vendidos"
                      className="block px-4 text-gray-900 rounded transition-colors font-jost"
                      onClick={() => setIsOpen(false)}
                    >
                      Vestido Casual Verano
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/ofertas"
                      className="block px-4 text-gray-900 rounded transition-colors font-jost"
                      onClick={() => setIsOpen(false)}
                    >
                      Ver Ofertas
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </>
        ) : (
          <Link
            to="/tienda"
            className="flex items-center px-4 py-3 min-h-[48px] w-full font-jost text-black-500 hover:text-primary transition-colors tracking-[0.5px] text-[13px]"
          >
            TIENDA
          </Link>
        )}
      </div>
    </>
  );
};